import { OFF_SECONDARY_CLASS } from 'components/buttons/buttonStyle';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import { XIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { ComboboxChip } from 'components/ui/combobox';
import { Dialog, DialogClose, DialogContent, DialogTitle } from 'components/ui/dialog';
import { RadioCard, RadioGroup } from 'components/ui/radio-group';
import { Textarea } from 'components/ui/textarea';
import { ReactNode, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import {
    useAddInsiderFunctionAndReasonMutation,
    useUpdateInsiderFunctionAndReasonMutation,
} from 'store/api/insider-function-and-reasons';
import type { EmployeeListItemMinDto } from 'types/employees';
import { InsiderFunctionAndReasonDto } from 'types/matter-insider-list';
import { successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';

type FormValues = {
    functionAndReason: string;
    appliesTo: 'DEFAULT' | 'EMPLOYEES';
    employees: EmployeeListItemMinDto[];
};

const FieldLabel = ({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) => (
    <label htmlFor={htmlFor} className="text-foreground-secondary text-meta font-medium">
        {children}
    </label>
);

/**
 * Adds or edits a function-and-reason entry. Only one entry can be the default, and an employee can be on one entry
 * only: another entry's employees are offered disabled, naming the entry. The server refuses both too, and its reason
 * is shown by the API layer while the dialog stays open. It is the new design's dialog over either page, portalled
 * into a `data-design='new'` root as the share dialog is; only Save keeps the page's amber.
 */
export default function FunctionAndReasonDialog({
    entry,
    entries,
    onClose,
}: {
    entry?: InsiderFunctionAndReasonDto;
    entries: InsiderFunctionAndReasonDto[];
    onClose: () => void;
}) {
    const id = useId();
    const formatName = useEmployeeNameFormatter();
    // The dialog waits for its root, which is in the document from the first commit.
    const [root, setRoot] = useState<HTMLDivElement | null>(null);
    const [addEntry] = useAddInsiderFunctionAndReasonMutation();
    const [updateEntry] = useUpdateInsiderFunctionAndReasonMutation();
    const others = entries.filter((other) => other.id !== entry?.id);
    const otherDefault = others.find((other) => other.isDefault);
    const takenBy = new Map(
        others.flatMap((other) => other.employees.map((employee) => [employee.id, other.functionAndReason] as const))
    );
    const { control, register, setValue, handleSubmit, formState } = useForm<FormValues>({
        defaultValues: {
            functionAndReason: entry?.functionAndReason ?? '',
            appliesTo: (entry ? entry.isDefault : !otherDefault) ? 'DEFAULT' : 'EMPLOYEES',
            employees: (entry?.employees ?? []) as EmployeeListItemMinDto[],
        },
    });
    const [text, appliesTo, employees] = useWatch({ control, name: ['functionAndReason', 'appliesTo', 'employees'] });
    const isDefault = appliesTo === 'DEFAULT';
    const canSave = !!text.trim() && (isDefault || employees.length > 0);

    const save = handleSubmit(async (values) => {
        const body = {
            functionAndReason: values.functionAndReason.trim(),
            isDefault,
            employeeIds: isDefault ? [] : values.employees.map((employee) => employee.id),
        };
        try {
            await (entry ? updateEntry({ id: entry.id, ...body }) : addEntry(body)).unwrap();
        } catch {
            // The API layer has already shown the server's reason; the dialog stays open to fix it.
            return;
        }
        successAlert(t('configuration.insider-list.saved'));
        onClose();
    });

    return (
        <>
            {createPortal(<div data-design="new" ref={setRoot} />, document.body)}
            <Dialog open onOpenChange={(open) => !open && onClose()}>
                <DialogContent
                    container={root}
                    showCloseButton={false}
                    className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[560px]"
                >
                    <form
                        noValidate
                        className="flex min-h-0 flex-col"
                        onSubmit={(event) => (canSave ? void save(event) : event.preventDefault())}
                    >
                        <div className="flex shrink-0 items-center gap-4 ps-6 pe-4 pt-5">
                            <DialogTitle className="text-dialog-title not-new:tracking-normal flex-1 font-semibold">
                                <FormattedMessage
                                    id={
                                        entry
                                            ? 'configuration.insider-list.edit-title'
                                            : 'configuration.insider-list.add'
                                    }
                                />
                            </DialogTitle>
                            <DialogClose
                                render={<Button variant="ghost" size="icon" className="text-foreground-tertiary" />}
                            >
                                <XIcon className="size-4" />
                                <span className="sr-only">
                                    <FormattedMessage id="close" />
                                </span>
                            </DialogClose>
                        </div>
                        <div className="flex min-h-0 flex-col gap-5 overflow-y-auto px-6 pt-4 pb-5">
                            <div className="flex flex-col gap-1.5">
                                <FieldLabel htmlFor={`${id}-text`}>
                                    <FormattedMessage id="matters.insider-list.function-and-reason" />
                                </FieldLabel>
                                <Textarea
                                    id={`${id}-text`}
                                    rows={3}
                                    autoFocus
                                    aria-required
                                    placeholder={t('matters.insider-list.function-and-reason-placeholder')}
                                    {...register('functionAndReason')}
                                />
                            </div>
                            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                                <legend className="mb-2 p-0">
                                    <FieldLabel>
                                        <FormattedMessage id="configuration.insider-list.applies-to" />
                                    </FieldLabel>
                                </legend>
                                <RadioGroup
                                    value={appliesTo}
                                    onValueChange={(value) => setValue('appliesTo', value as FormValues['appliesTo'])}
                                    aria-label={t('configuration.insider-list.applies-to')}
                                    className="gap-2"
                                >
                                    <RadioCard
                                        value="DEFAULT"
                                        disabled={!!otherDefault}
                                        title={t('configuration.insider-list.default')}
                                        description={
                                            otherDefault
                                                ? t('configuration.insider-list.default-taken', {
                                                      text: otherDefault.functionAndReason,
                                                  })
                                                : t('configuration.insider-list.default-description')
                                        }
                                    />
                                    <RadioCard
                                        value="EMPLOYEES"
                                        title={t('configuration.insider-list.selected-employees')}
                                        description={t('configuration.insider-list.selected-employees-description')}
                                    />
                                </RadioGroup>
                            </fieldset>
                            {isDefault ? null : (
                                <div className="flex flex-col gap-1.5">
                                    <DynamicMultiSelectInput<FormValues, EmployeeListItemMinDto>
                                        control={control}
                                        setValue={setValue}
                                        name="employees"
                                        label="configuration.insider-list.employees"
                                        placeholder="configuration.insider-list.employees-placeholder"
                                        paginated={{
                                            fetch: useLazyGetEmployeesListWithFiltersQuery,
                                            searchField: 'fullName',
                                        }}
                                        getOptionLabel="name"
                                        getOptionIdentifier="id"
                                        keepSelected
                                        // Six rows, as drawn: short enough to open under the field rather than over it.
                                        listClassName="max-h-56"
                                        clearable={false}
                                        renderTags={(selected) =>
                                            selected.map((employee) => (
                                                <ComboboxChip
                                                    key={employee.id}
                                                    className="bg-neutral-tint text-foreground-secondary h-6 max-w-full rounded-[6px] ps-2"
                                                >
                                                    <span className="truncate">{formatName(employee)}</span>
                                                </ComboboxChip>
                                            ))
                                        }
                                        customOptionRenderer={(option) => {
                                            const taken = takenBy.get(option.id);
                                            return {
                                                element: (
                                                    <span className="flex w-full min-w-0 items-center gap-2">
                                                        <span className="min-w-0 flex-1 truncate">
                                                            {formatName(option)}
                                                        </span>
                                                        {taken ? (
                                                            <span className="text-muted-foreground text-caption max-w-[260px] truncate">
                                                                {t('configuration.insider-list.on-entry', {
                                                                    text: taken,
                                                                })}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                ),
                                                isDisabled: taken !== undefined,
                                            };
                                        }}
                                    />
                                    <p className="text-foreground-secondary m-0 text-xs leading-[18px]">
                                        {employees.length ? (
                                            <FormattedMessage
                                                id="configuration.insider-list.picked-count"
                                                values={{ count: employees.length }}
                                            />
                                        ) : (
                                            <FormattedMessage id="configuration.insider-list.pick-one" />
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="border-neutral-tint flex h-[68px] shrink-0 items-center justify-end gap-2.5 border-t px-6">
                            <Button
                                variant="outline"
                                type="button"
                                className="bg-paper text-foreground border-grey-300 h-9 px-3.5 font-medium shadow-none"
                                onClick={onClose}
                            >
                                <FormattedMessage id="cancel" />
                            </Button>
                            {/* Off yet still focusable, so a refused save leaves the focus on it. */}
                            <Button
                                variant="secondary"
                                type="submit"
                                className={cn(
                                    'h-9 px-4 font-semibold [--secondary:var(--page-secondary)]',
                                    OFF_SECONDARY_CLASS
                                )}
                                disabled={formState.isSubmitting || !canSave}
                                focusableWhenDisabled
                            >
                                <FormattedMessage id="save" />
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
